import API from '@app/api';
import Notification from '@app/action/notification';
import Pending from '@app/action/pending';

import ActionHelper from '@module/redux-actions';
import Router from '@module/router';
import { HttpResponseCodes } from '@module/http/axios/response';
import I18n from '@module/i18n';

export const actions = ActionHelper.types([
  'GET_ACCOUNT',
  'GET_SUBSCRIPTION',
  'GET_LAST_ORDER',
  'GET_CHECKOUT_DETAILS',

  'ACCOUNT_UPDATE_SUCCESS',
  'CANCEL_SUBSCRIPTION_SUCCESS',
  'REACTIVATE_SUBSCRIPTION_SUCCESS',

  'PAYMENT_SUCCESS',
]);

export default ActionHelper.generate({
  setDefaultRecipientVerified: (email) => ({
    type: actions.ACCOUNT_UPDATE_SUCCESS,
    details: {
      default_recipient_email: email,
      receiving_email_confirmed: true,
    },
  }),

  getAccount(accountId) {
    const pendingId = 'getAccount';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getAccount(null, {
          pathExtValues: {
            accountId,
          },
        });

        dispatch({
          type: actions.GET_ACCOUNT,
          details: response.body,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_account_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  createSubdomain(accountId, params = {}, options = {}) {
    const pendingId = 'editAccount';
    const showNotification = !!options.showNotification;

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.createSubdomain(params, {
          pathExtValues: {
            accountId,
          },
        });

        dispatch({
          type: actions.ACCOUNT_UPDATE_SUCCESS,
          details: response.body,
        });

        if (showNotification) {
          dispatch(
            Notification.setNotification(
              'notification',
              'update_profile_success'
            )
          );
        }
      } catch (error) {
        if (showNotification) {
          dispatch(
            Notification.setNotification('error', 'update_profile_error')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  editAccount(accountId, params = {}, options = {}) {
    const pendingId = 'editAccount';
    const showNotification =
      options.notification === undefined || options.notification === true
        ? true
        : false;

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.editAccount(params, {
          pathExtValues: {
            accountId: accountId,
          },
        });

        dispatch({
          type: actions.ACCOUNT_UPDATE_SUCCESS,
          details: response.body,
        });

        if (showNotification) {
          dispatch(
            Notification.setNotification(
              'notification',
              'update_profile_success'
            )
          );
        }
      } catch (error) {
        if (showNotification) {
          dispatch(
            Notification.setNotification('error', 'update_profile_error')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getSubscription(accountId = null, subscriptionId = null, options = {}) {
    const pendingId = 'getSubscription';
    const showNotification = Boolean(options.showNotification);

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      let params;
      if (accountId) {
        params = { account_id: accountId };
      }

      try {
        const response = await API.Account.getSubscriptionDetails(params, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_SUBSCRIPTION,
          details: response.body,
        });
      } catch (error) {
        if (showNotification) {
          dispatch(
            Notification.setNotification(
              'error',
              'retrieve_subscription_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  cancelSubscription(params = {}) {
    const pendingId = 'cancelSubscription';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      const {
        account: { subscription },
      } = getState();

      try {
        const response = await API.Account.cancelSubscription(params, {
          pathExtValues: {
            subscriptionId: subscription.id,
          },
        });

        dispatch({
          type: actions.CANCEL_SUBSCRIPTION_SUCCESS,
          details: response.body,
        });

        dispatch(
          Notification.setNotification(
            'notification',
            'cancel_subscription_success'
          )
        );
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'cancel_subscription_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  reactivateSubscription(params = {}) {
    const pendingId = 'reactivateSubscription';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      const {
        account: { subscription },
      } = getState();

      try {
        const response = await API.Account.reactivateSubscription(params, {
          pathExtValues: {
            subscriptionId: subscription.id,
          },
        });

        dispatch({
          type: actions.REACTIVATE_SUBSCRIPTION_SUCCESS,
          details: response.body,
        });

        dispatch(
          Notification.setNotification(
            'notification',
            'reactivate_subscription_success'
          )
        );
      } catch (e) {
        dispatch(
          Notification.setNotification(
            'error',
            'reactivate_subscription_exception'
          )
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  duplicateSubscription(params = {}, subscriptionId = null) {
    const pendingId = 'duplicateSubscription';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.duplicateSubscription(params, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_SUBSCRIPTION,
          active: false,
          details: response.body,
        });

        Router.navigateTo('/payment/methods', false, {
          search: 'renewal=true',
        });
      } catch (e) {
        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getLastOrder(subscriptionId = null) {
    const pendingId = 'getLastOrder';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getLastOrder(null, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_LAST_ORDER,
          details: response.body,
        });
      } catch (error) {
        if (error.status === HttpResponseCodes.NOT_FOUND) {
          Router.navigateTo('/account/renew-billing');
        } else {
          dispatch(
            Notification.setNotification(
              'error',
              'retrieve_last_order_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getCheckoutDetails(subscriptionId = null) {
    const pendingId = 'getCheckoutDetails';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getCheckoutDetails(null, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_CHECKOUT_DETAILS,
          details: response.body.checkout,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_checkout_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  updatePaymentPlan(subscriptionId, newPlanId) {
    const pendingId = 'updatePaymentPlan';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.Account.updatePaymentPlan(
          {
            new_nu_plan_id: newPlanId,
          },
          {
            pathExtValues: {
              subscriptionId,
            },
          }
        );
      } catch (error) {
        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  sendReceivingEmailConfirmation() {
    const pendingId = 'sendingReceivingTransfersEmailConfirmation';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.Account.sendReceivingEmailConfirmation();
        dispatch(
          Notification.setNotification(
            'success',
            I18n.t('page.verify_email.success.title')
          )
        );
      } catch (error) {
        dispatch(
          Notification.setNotification(
            'error',
            I18n.t('page.verify_email.failure.title')
          )
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },
});
